import { useEffect, useMemo, useRef, useState } from "react";
import { Search, Plus, Sparkles, Leaf, Flame, X, LayoutGrid, List as ListIcon, Printer, ChevronDown, SlidersHorizontal } from "lucide-react";
import { Link } from "react-router-dom";
import { api, money } from "@/lib/api";
import { formatKcal } from "@/lib/dietary";
import { useDietary } from "@/context/DietaryContext";
import { useDietaryProfile } from "@/context/DietaryProfileContext";
import { useAuth } from "@/context/AuthContext";
import { useBusiness } from "@/context/BusinessContext";
import { checkConflicts } from "@/lib/dietaryConflict";
import RestaurantAiChatWidget from "@/components/RestaurantAiChatWidget";
import MenuPageCompact from "@/pages/MenuPageCompact";
import MenuPagePhotoGrid from "@/pages/MenuPagePhotoGrid";
import MenuPageBare from "@/pages/MenuPageBare";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import AddonDialog from "@/components/AddonDialog";
import NutritionModal from "@/components/NutritionModal";
import { DietaryBadges, DietaryLegend } from "@/components/DietaryBadges";
import { useT } from "@/lib/useT";
import LoyaltyTeaseStrip from "@/components/LoyaltyTeaseStrip";

const KIND_TABS = [
  { value: "all", label: "Everything" },
  { value: "food", label: "Food" },
  { value: "wine", label: "Wine" },
  { value: "beverage", label: "Beverages" },
];

const DIET_PROFILE_KEY = "bella_diet_profile_v1";
const VIEW_KEY = "bella_menu_view_v1";

function loadDietProfile() {
  try {
    const raw = localStorage.getItem(DIET_PROFILE_KEY);
    if (!raw) return { tags: [], maxCalories: "" };
    const parsed = JSON.parse(raw);
    return {
      tags: Array.isArray(parsed.tags) ? parsed.tags : [],
      maxCalories: parsed.maxCalories ?? "",
    };
  } catch (_e) {
    return { tags: [], maxCalories: "" };
  }
}

export default function MenuPage() {
  const { business } = useBusiness();
  // Operator-selected /menu layout (Admin → Settings → Menu layout):
  //   • compact     → MenuPageBare      (restaurant-style bare list + modal)
  //   • photo-grid  → MenuPagePhotoGrid (multi-column photo cards)
  //   • accordion   → MenuPageCompact   (collapsible categories, time-aware)
  //   • anything else → legacy MenuPageGrid (image-rich search/filter view)
  const layout = business?.menu_layout || "grid";
  if (layout === "compact")    return <MenuPageBare />;
  if (layout === "photo-grid") return <MenuPagePhotoGrid />;
  if (layout === "accordion")  return <MenuPageCompact />;
  return <MenuPageGrid />;
}

function MenuPageGrid() {
  const t = useT();
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [activeCat, setActiveCat] = useState("");
  const [activeKind, setActiveKind] = useState("all");
  const [chefOnly, setChefOnly] = useState(false);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [nutritionItem, setNutritionItem] = useState(null);
  const [view, setView] = useState(() => {
    try {
      return localStorage.getItem(VIEW_KEY) === "list" ? "list" : "grid";
    } catch (_e) {
      return "grid";
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(VIEW_KEY, view);
    } catch (_e) {
      /* ignore */
    }
  }, [view]);
  const { legend: DIETARY } = useDietary();
  const { profile: dietaryProfile } = useDietaryProfile();
  const { user } = useAuth();
  // Whether to completely hide items that clash with the diner's
  // saved allergen card (default: show greyed-out so they're
  // visible but clearly unsafe). Persisted in localStorage so the
  // diner's preference travels across sessions.
  const [hideConflicts, setHideConflicts] = useState(() => {
    try { return localStorage.getItem("hp_menu_hide_conflicts") === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    try {
      localStorage.setItem("hp_menu_hide_conflicts",
                                  hideConflicts ? "1" : "0");
    } catch { /* private mode */ }
  }, [hideConflicts]);

  // Guest-side dietary profile (persisted in localStorage). When a customer
  // is signed in we replace this state with their server-stored profile.
  const initialProfile = loadDietProfile();
  const [dietTags, setDietTags] = useState(new Set(initialProfile.tags));
  const [maxCalories, setMaxCalories] = useState(initialProfile.maxCalories);
  const profileHydratedRef = useRef(false);
  const menuSectionsRef = useRef(null);

  function scrollToMenuSections() {
    // Defer to next tick so the DOM has re-rendered the filtered sections.
    requestAnimationFrame(() => {
      const el = menuSectionsRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY - 140; // offset for sticky filter bar
      window.scrollTo({ top, behavior: "smooth" });
    });
  }

  useEffect(() => {
    localStorage.setItem(
      DIET_PROFILE_KEY,
      JSON.stringify({ tags: Array.from(dietTags), maxCalories }),
    );
  }, [dietTags, maxCalories]);

  // When the customer signs in, hydrate the filter from their saved server profile.
  useEffect(() => {
    if (!user || user.role !== "customer" || profileHydratedRef.current) return;
    profileHydratedRef.current = true;
    (async () => {
      try {
        const { data } = await api.get("/me/profile");
        if (Array.isArray(data.dietary_tags) && data.dietary_tags.length > 0) {
          setDietTags(new Set(data.dietary_tags));
        }
        if (data.max_calories) setMaxCalories(String(data.max_calories));
      } catch (_e) {
        /* keep localStorage state */
      }
    })();
  }, [user]);

  function toggleDietTag(code) {
    setDietTags((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  function clearFilters() {
    setDietTags(new Set());
    setMaxCalories("");
    setChefOnly(false);
    setQ("");
  }

  useEffect(() => {
    (async () => {
      try {
        const [catRes, itemRes] = await Promise.all([
          api.get("/categories"),
          api.get("/menu", { params: { available_only: true } }),
        ]);
        setCategories(catRes.data);
        setItems(itemRes.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const visibleCategories = useMemo(() => {
    if (activeKind === "all") return categories;
    return categories.filter((c) => c.kind === activeKind);
  }, [categories, activeKind]);

  const dietTagsArray = useMemo(() => Array.from(dietTags), [dietTags]);
  const maxCalsNum = useMemo(() => {
    const n = parseInt(maxCalories, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [maxCalories]);

  const filtered = useMemo(() => {
    const catIds = new Set(visibleCategories.map((c) => c.id));
    return items
      .filter((it) => catIds.has(it.category_id))
      .filter((it) => (activeCat ? it.category_id === activeCat : true))
      .filter((it) => (chefOnly ? (it.tags || []).includes("chef_special") : true))
      .filter((it) => {
        if (dietTagsArray.length === 0) return true;
        const set = new Set(it.tags || []);
        return dietTagsArray.every((t) => set.has(t));
      })
      .filter((it) => {
        if (maxCalsNum == null) return true;
        // Items without calorie data are kept (treat as "unknown ≤ limit")
        if (!it.calories) return true;
        return it.calories <= maxCalsNum;
      })
      .filter((it) => {
        if (!q) return true;
        const hay = `${it.name} ${it.description} ${it.origin || ""}`.toLowerCase();
        return hay.includes(q.toLowerCase());
      });
  }, [items, visibleCategories, activeCat, chefOnly, dietTagsArray, maxCalsNum, q]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const c of visibleCategories) map.set(c.id, { category: c, items: [] });
    for (const it of filtered) {
      // Stamp every item with its conflict result against the
      // diner's saved allergen card. Cards then render greyed
      // (and the Add button locks) when there's a conflict.
      const conflict = checkConflicts(it, dietaryProfile);
      if (hideConflicts && conflict.hasAllergen) continue;
      const annotated = { ...it, _conflict: conflict };
      const g = map.get(it.category_id);
      if (g) g.items.push(annotated);
    }
    return Array.from(map.values()).filter((g) => g.items.length > 0);
  }, [filtered, visibleCategories, dietaryProfile, hideConflicts]);

  const activeFilterCount =
    dietTags.size + (maxCalsNum != null ? 1 : 0) + (chefOnly ? 1 : 0) + (q ? 1 : 0);

  return (
    <div data-testid="menu-page" className="bg-[#FDFBF7]">
      {/* Header band */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-20 pb-8">
        <p className="text-xs uppercase tracking-[0.15em] font-bold text-[#5A7247]">
          Our Menu
        </p>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 max-w-3xl">
          Search, customise, and pre-order your meal.
        </h1>
        <p className="mt-4 text-[#5C625E] max-w-2xl">
          Add condiments, choose sides, or simply browse our cellar. Everything is
          ready when you arrive.
        </p>
        <div className="mt-6">
          <LoyaltyTeaseStrip compact />
        </div>
      </section>

      {/* Sticky filter bar */}
      <div className="sticky top-16 md:top-20 z-30 bg-[#FDFBF7]/95 backdrop-blur-xl border-y border-[#E8E5DF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 space-y-3">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search
                className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5C625E]"
                strokeWidth={1.5}
              />
              <Input
                data-testid="menu-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search dishes, wines, regions…"
                className="pl-11 h-12 rounded-full bg-[#F3F0EA] border-none focus-visible:ring-2 focus-visible:ring-[#C05746]/20"
              />
            </div>
            <div
              className="flex gap-2 overflow-x-auto no-scrollbar"
              data-testid="kind-tabs"
            >
              {KIND_TABS.map((k) => (
                <button
                  key={k.value}
                  type="button"
                  data-testid={`kind-tab-${k.value}`}
                  onClick={() => {
                    setActiveKind(k.value);
                    setActiveCat("");
                    scrollToMenuSections();
                  }}
                  className={`px-5 h-12 rounded-full text-sm whitespace-nowrap transition-all ${
                    activeKind === k.value
                      ? "bg-[#121614] text-white"
                      : "bg-white text-[#121614] border border-[#E8E5DF] hover:border-[#121614]"
                  }`}
                >
                  {k.label}
                </button>
              ))}
            </div>

            <div
              data-testid="menu-view-toggle"
              className="flex items-center bg-[#F3F0EA] rounded-full p-1 h-12 shrink-0"
            >
              <button
                type="button"
                data-testid="menu-view-grid"
                onClick={() => setView("grid")}
                className={`h-10 px-3 rounded-full text-xs uppercase tracking-wider inline-flex items-center gap-1.5 transition-colors ${
                  view === "grid"
                    ? "bg-white text-[#121614] shadow-sm"
                    : "text-[#5C625E] hover:text-[#121614]"
                }`}
                aria-label="Card view"
              >
                <LayoutGrid className="h-3.5 w-3.5" strokeWidth={1.5} /> Cards
              </button>
              <button
                type="button"
                data-testid="menu-view-list"
                onClick={() => setView("list")}
                className={`h-10 px-3 rounded-full text-xs uppercase tracking-wider inline-flex items-center gap-1.5 transition-colors ${
                  view === "list"
                    ? "bg-white text-[#121614] shadow-sm"
                    : "text-[#5C625E] hover:text-[#121614]"
                }`}
                aria-label="List view"
              >
                <ListIcon className="h-3.5 w-3.5" strokeWidth={1.5} /> List
              </button>
              <Link
                to="/menu/quick"
                data-testid="menu-quick-link"
                className="h-10 px-3 rounded-full text-xs uppercase tracking-wider inline-flex items-center gap-1.5 text-[#5C625E] hover:text-[#121614]"
                aria-label="Open print-friendly quick menu"
                title="Open print-friendly quick menu"
              >
                <Printer className="h-3.5 w-3.5" strokeWidth={1.5} /> Print
              </Link>
            </div>
          </div>

          <div className="flex gap-2 overflow-x-auto no-scrollbar" data-testid="category-tabs">
            <button
              type="button"
              data-testid="cat-tab-all"
              onClick={() => { setActiveCat(""); scrollToMenuSections(); }}
              className={`px-4 h-9 rounded-full text-xs uppercase tracking-wider whitespace-nowrap transition-all ${
                !activeCat
                  ? "bg-[#C05746] text-white"
                  : "bg-white text-[#5C625E] border border-[#E8E5DF] hover:text-[#121614]"
              }`}
            >
              All Categories
            </button>
            {visibleCategories.map((c) => (
              <button
                key={c.id}
                type="button"
                data-testid={`cat-tab-${c.slug}`}
                onClick={() => { setActiveCat(c.id); scrollToMenuSections(); }}
                className={`px-4 h-9 rounded-full text-xs uppercase tracking-wider whitespace-nowrap transition-all ${
                  activeCat === c.id
                    ? "bg-[#C05746] text-white"
                    : "bg-white text-[#5C625E] border border-[#E8E5DF] hover:text-[#121614]"
                }`}
              >
                {t(c, "name")}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2" data-testid="diet-profile-row">
            <span className="text-[11px] uppercase tracking-[0.15em] font-bold text-[#5A7247] mr-1">
              Dietary
            </span>
            <button
              type="button"
              data-testid="diet-chef-only"
              onClick={() => setChefOnly((v) => !v)}
              className={`px-3 h-8 rounded-full text-xs whitespace-nowrap transition-all inline-flex items-center gap-1 ${
                chefOnly
                  ? "bg-[#C05746] text-white"
                  : "bg-[#F3F0EA] text-[#5C625E] hover:bg-[#E8E5DF]"
              }`}
            >
              <Sparkles className="h-3 w-3" strokeWidth={1.5} />
              Chef Specials
            </button>

            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="diet-dropdown-trigger"
                  className={`px-3 h-8 rounded-full text-xs whitespace-nowrap transition-all border inline-flex items-center gap-1.5 ${
                    dietTags.size > 0
                      ? "bg-[#5A7247] text-white border-[#5A7247]"
                      : "bg-white text-[#5C625E] border-[#E8E5DF] hover:border-[#5A7247]"
                  }`}
                >
                  <SlidersHorizontal className="h-3 w-3" strokeWidth={1.5} />
                  Diet
                  {dietTags.size > 0 && (
                    <span
                      data-testid="diet-dropdown-count"
                      className="ml-0.5 rounded-full bg-white/30 px-1.5 text-[10px] font-bold"
                    >
                      {dietTags.size}
                    </span>
                  )}
                  <ChevronDown className="h-3 w-3" strokeWidth={1.5} />
                </button>
              </PopoverTrigger>
              <PopoverContent
                data-testid="diet-dropdown-panel"
                className="w-72 p-2 max-h-80 overflow-y-auto"
                align="start"
              >
                <p className="text-[10px] uppercase tracking-wider font-bold text-[#5A7247] px-2 pt-1 pb-2">
                  Show only items that are…
                </p>
                <ul className="space-y-0.5">
                  {DIETARY.map((d) => {
                    const checked = dietTags.has(d.code);
                    return (
                      <li key={d.code}>
                        <div
                          role="button"
                          tabIndex={0}
                          data-testid={`diet-filter-${d.code}`}
                          onClick={() => toggleDietTag(d.code)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              toggleDietTag(d.code);
                            }
                          }}
                          className={`w-full flex items-center gap-2 px-2 py-2 rounded-lg text-left text-sm transition-colors cursor-pointer ${
                            checked
                              ? "bg-[#5A7247]/10 text-[#2C3A22]"
                              : "hover:bg-[#F3F0EA] text-[#121614]"
                          }`}
                        >
                          <Checkbox
                            checked={checked}
                            tabIndex={-1}
                            onCheckedChange={() => toggleDietTag(d.code)}
                            className="pointer-events-none"
                            data-testid={`diet-filter-${d.code}-cb`}
                          />
                          <span className="text-[10px] font-bold uppercase tracking-wider w-9 text-[#5A7247]">
                            {d.short}
                          </span>
                          <span className="flex-1">{d.label}</span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
                {dietTags.size > 0 && (
                  <div className="mt-2 pt-2 border-t border-[#E8E5DF] flex justify-end">
                    <button
                      type="button"
                      data-testid="diet-dropdown-clear"
                      onClick={() => setDietTags(new Set())}
                      className="text-xs text-[#C05746] hover:text-[#A64A3C] font-medium px-2 py-1"
                    >
                      Clear all
                    </button>
                  </div>
                )}
              </PopoverContent>
            </Popover>

            <div className="flex items-center gap-1 ml-1">
              <label
                htmlFor="diet-max-cal"
                className="text-xs text-[#5C625E] whitespace-nowrap"
              >
                Under
              </label>
              <Input
                id="diet-max-cal"
                data-testid="diet-max-calories"
                type="number"
                min="0"
                step="50"
                placeholder="kcal"
                value={maxCalories}
                onChange={(e) => setMaxCalories(e.target.value)}
                className="h-8 w-24 rounded-full bg-white border-[#E8E5DF] text-xs"
              />
              <span className="text-xs text-[#5C625E]">kcal</span>
            </div>
            {activeFilterCount > 0 && (
              <button
                type="button"
                data-testid="diet-clear-filters"
                onClick={clearFilters}
                className="ml-auto inline-flex items-center gap-1 text-xs text-[#C05746] hover:text-[#A64A3C] font-medium"
              >
                <X className="h-3 w-3" strokeWidth={2} />
                Clear ({activeFilterCount})
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Menu sections */}
      <section ref={menuSectionsRef} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
        {/* Allergen-card banner — only when the diner has saved
            allergens. Keeps the kitchen-level safety promise loud. */}
        {(dietaryProfile.allergens?.length > 0 ||
            dietaryProfile.ailments?.length > 0) && (
          <div data-testid="allergen-banner"
                  className="mb-6 bg-rose-50 border border-rose-200
                              rounded-2xl p-4 flex flex-wrap items-center
                              gap-3">
            <span className="text-sm font-semibold text-rose-800">
              ⚠ Your allergen card is active.
            </span>
            <span className="text-sm text-rose-700">
              Hiding nothing — items that contain
              {" "}<b>{dietaryProfile.allergens.join(", ") || "—"}</b>
              {" "}appear greyed-out and cannot be added.
            </span>
            <label className="ml-auto inline-flex items-center
                                  gap-2 text-xs text-rose-700">
              <input type="checkbox" checked={hideConflicts}
                        data-testid="hide-conflicts-toggle"
                        onChange={(e) => setHideConflicts(e.target.checked)}
                        className="rounded" />
              Hide them entirely
            </label>
            {user && (
              <Link to="/account"
                      data-testid="edit-allergen-card"
                      className="text-xs underline text-rose-700">
                edit card
              </Link>
            )}
          </div>
        )}

        <div className="mb-8">
          <DietaryLegend collapsed />
        </div>
        {loading && (
          <p data-testid="menu-loading" className="text-[#5C625E]">
            Loading menu…
          </p>
        )}

        {!loading && grouped.length === 0 && (
          <div
            data-testid="menu-empty"
            className="text-center py-20 text-[#5C625E]"
          >
            <p className="font-serif-display text-3xl text-[#121614]">No matches</p>
            <p className="mt-2">Try a different search or relax your filters.</p>
            {activeFilterCount > 0 && (
              <Button
                type="button"
                data-testid="menu-empty-clear"
                onClick={clearFilters}
                className="mt-6 bg-[#C05746] hover:bg-[#A64A3C] text-white rounded-full px-6"
              >
                Clear all filters
              </Button>
            )}
          </div>
        )}

        {grouped.map((group) => (
          <div key={group.category.id} className="mb-14" data-testid={`section-${group.category.slug}`}>
            <div className="flex items-baseline justify-between mb-6 border-b border-[#E8E5DF] pb-3">
              <h2 className="font-serif-display text-3xl md:text-4xl">
                {t(group.category, "name")}
              </h2>
              <span className="text-xs uppercase tracking-widest text-[#5C625E]">
                {group.items.length} items
              </span>
            </div>

            <div className={view === "list"
              ? "divide-y divide-[#E8E5DF] border border-[#E8E5DF] rounded-2xl bg-white"
              : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8"
            }>
              {view === "list" ? group.items.map((item) => (
                <div
                  key={item.id}
                  data-testid={`menu-list-row-${item.id}`}
                  title={item._conflict?.reasons?.join(" · ") || ""}
                  className={`flex items-center justify-between gap-4 px-4 sm:px-6 py-3
                                  ${item._conflict?.hasAllergen
                                      ? "opacity-50 bg-rose-50/40"
                                      : item._conflict?.missingDiet
                                      ? "opacity-70"
                                      : ""}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <p className="font-medium text-[#121614] truncate">
                        {t(item, "name")}
                      </p>
                      {item.tags?.includes("chef_special") && (
                        <span className="text-[10px] uppercase tracking-wider font-bold text-[#C05746]">★ Special</span>
                      )}
                      <DietaryBadges tags={item.tags} testIdPrefix={`row-${item.id}-diet`} />
                      {item._conflict?.hasAllergen && (
                        <span data-testid={`row-${item.id}-allergen`}
                                  className="text-[10px] uppercase tracking-wider
                                          font-bold text-rose-700 bg-rose-100
                                          border border-rose-300 rounded-full
                                          px-1.5 py-0.5">
                          ⚠ {item._conflict.allergens.join(", ")}
                        </span>
                      )}
                    </div>
                    {item.description && (
                      <p className="text-xs text-[#5C625E] mt-0.5 line-clamp-1">
                        {t(item, "description")}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <PriceCells category={group.category} item={item} />
                    {item.is_priced === false ? (
                      <span className="text-[11px] uppercase tracking-wider text-[#5A7247] font-medium">included</span>
                    ) : item._conflict?.hasAllergen ? (
                      <Button data-testid={`list-blocked-${item.id}`}
                              disabled
                              size="sm"
                              className="bg-rose-200 text-rose-800
                                          rounded-full h-8 px-3 text-xs
                                          cursor-not-allowed">
                        ⚠ Blocked
                      </Button>
                    ) : (
                      <Button
                        data-testid={`list-add-${item.id}`}
                        onClick={() => setSelectedItem({ ...item, _category: group.category })}
                        size="sm"
                        className="bg-[#C05746] hover:bg-[#A64A3C] text-white rounded-full h-8 px-3 text-xs"
                      >
                        <Plus className="h-3.5 w-3.5 mr-1" strokeWidth={2} /> Add
                      </Button>
                    )}
                  </div>
                </div>
              )) : group.items.map((item) => (
                <article
                  key={item.id}
                  data-testid={`menu-item-card-${item.id}`}
                  title={item._conflict?.reasons?.join(" · ") || ""}
                  className={`bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-[#121614]/5 flex flex-col relative
                                  ${item._conflict?.hasAllergen
                                      ? "opacity-50 grayscale"
                                      : item._conflict?.missingDiet
                                      ? "opacity-75"
                                      : ""}`}
                >
                  <div className="relative h-44 overflow-hidden bg-[#F3F0EA]">
                    {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                      />
                    ) : (
                      <div
                        className="w-full h-full flex items-center justify-center text-[#5C625E]/60 font-serif-display text-4xl"
                        aria-hidden
                      >
                        {item.name?.[0] || "·"}
                      </div>
                    )}
                    <div className="absolute top-3 left-3 flex gap-1 flex-wrap max-w-[80%]">
                      {item.tags?.includes("chef_special") && (
                        <Badge className="bg-[#C05746] hover:bg-[#C05746] text-white border-0 gap-1">
                          <Sparkles className="h-3 w-3" strokeWidth={1.5} /> Special
                        </Badge>
                      )}
                      {item.tags?.includes("vegetarian") && (
                        <Badge className="bg-[#5A7247] hover:bg-[#5A7247] text-white border-0 gap-1">
                          <Leaf className="h-3 w-3" strokeWidth={1.5} /> Veg
                        </Badge>
                      )}
                      {item.tags?.includes("spicy") && (
                        <Badge className="bg-orange-600 hover:bg-orange-600 text-white border-0 gap-1">
                          <Flame className="h-3 w-3" strokeWidth={1.5} /> Spicy
                        </Badge>
                      )}
                      {item._conflict?.hasAllergen && (
                        <Badge data-testid={`card-${item.id}-allergen`}
                                  className="bg-rose-700 hover:bg-rose-700 text-white border-0 gap-1">
                          ⚠ {item._conflict.allergens.join(", ")}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <h3 className="font-serif-display text-xl leading-tight">{t(item, "name")}</h3>
                    {item.origin && (
                      <p className="text-xs uppercase tracking-wider text-[#5A7247] mt-1">
                        {item.origin}
                      </p>
                    )}
                    <p className="text-sm text-[#5C625E] mt-2 line-clamp-3 flex-1">
                      {t(item, "description")}
                    </p>
                    <div className="mt-3 flex items-center justify-between gap-2">
                      <DietaryBadges tags={item.tags} testIdPrefix={`card-${item.id}-diet`} />
                      {item.calories > 0 && (
                        <button
                          type="button"
                          data-testid={`item-calories-${item.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setNutritionItem(item);
                          }}
                          className="text-[11px] font-medium text-[#5C625E] hover:text-[#5A7247] underline decoration-dotted underline-offset-2 whitespace-nowrap"
                          title="See full nutrition info"
                        >
                          ≈ {formatKcal(item.calories)}
                        </button>
                      )}
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-3">
                      <PriceCells category={group.category} item={item} />
                      {item.is_priced === false ? (
                        <span
                          data-testid={`item-banquet-inclusion-${item.id}`}
                          className="text-[11px] uppercase tracking-wider text-[#5A7247] font-medium"
                        >
                          included
                        </span>
                      ) : item._conflict?.hasAllergen ? (
                        <Button data-testid={`card-blocked-${item.id}`}
                                disabled
                                className="bg-rose-200 text-rose-800
                                            rounded-full h-9 px-4 text-sm
                                            cursor-not-allowed shrink-0">
                          ⚠ Blocked
                        </Button>
                      ) : (
                        <Button
                          data-testid={`add-to-cart-${item.id}`}
                          onClick={() => setSelectedItem({ ...item, _category: group.category })}
                          className="bg-[#C05746] hover:bg-[#A64A3C] text-white rounded-full h-9 px-4 text-sm shrink-0"
                        >
                          <Plus className="h-4 w-4 mr-1" strokeWidth={2} />
                          Add
                        </Button>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        ))}
      </section>

      <AddonDialog item={selectedItem} onClose={() => setSelectedItem(null)} />
      <NutritionModal item={nutritionItem} onClose={() => setNutritionItem(null)} />
      <RestaurantAiChatWidget />
    </div>
  );
}

/** Renders 1–3 price cells based on the category's `price_columns` config.
 * Falls back to the legacy single `price` if no columns are configured. */
function PriceCells({ category, item }) {
  const cols = category?.price_columns?.length ? category.price_columns : [{ key: "default", label: "" }];
  const isWhole = category?.price_format === "whole";
  const format = (v) =>
    v == null || v === ""
      ? "—"
      : isWhole
        ? `$${Math.round(Number(v))}`
        : `$${Number(v).toFixed(2)}`;

  return (
    <div
      className="flex items-baseline gap-3 flex-wrap"
      data-testid={`item-prices-${item.id}`}
    >
      {cols.map((c) => {
        const raw = (item.prices || {})[c.key];
        const value = raw != null ? raw : c.key === "default" ? item.price : null;
        // Hide cells with no value when category has multiple columns
        if (cols.length > 1 && (value == null || value === "")) return null;
        return (
          <div key={c.key} className="text-right leading-none">
            {c.label && (
              <p className="text-[9px] tracking-widest font-bold text-[#5A7247] uppercase">
                {c.label}
              </p>
            )}
            <p className="text-lg font-medium text-[#121614]">{format(value)}</p>
          </div>
        );
      })}
    </div>
  );
}
